import {useEffect, useRef, useState} from 'react';
import {Category} from '../redux/entities/inventory/category';

/**
 * Extracted hook that manages the accordion open/close behavior based on a search query.
 *
 * Behavior:
 * - While searching (non-empty query): auto-open exactly the categories that match by name or contain matching items.
 * - When the search is cleared: collapse all categories once.
 * - With no search query: do not auto-open/close; user has full manual control.
 */
export function UseItemAccordionSearch<TItem extends { name?: string }>(params: {
  categories: Category[];
  categorized: Record<string, TItem[]>; // key: categoryId -> items under that category
}) {
  const {categories, categorized} = params;

  // Search text for filtering items in the accordion
  const [itemSearch, setItemSearch] = useState('');
  // Track opened accordion categories (controlled Accordion)
  const [openedCategories, setOpenedCategories] = useState<string[]>([]);

  // Auto-expand categories that match the search (by category name or item name)
  // and collapse only once when clearing the search to restore full manual control.
  const prevSearchRef = useRef<string>('');
  useEffect(() => {
    const q = itemSearch.trim().toLowerCase();
    const prevQ = prevSearchRef.current.trim().toLowerCase();

    // If search transitioned from non-empty to empty, collapse all categories once
    if (q.length === 0) {
      if (prevQ.length > 0) {
        setOpenedCategories([]);
      }
      prevSearchRef.current = q;
      return; // Do not alter opened state further when no search is active
    }

    const matches = new Set<string>();
    // Match category names
    categories.forEach(cat => {
      if ((cat.name ?? '').toLowerCase().includes(q)) matches.add(cat.id);
    });
    // Match items within categories
    Object.entries(categorized).forEach(([catId, items]) => {
      if (items.some(it => (it?.name ?? '').toLowerCase().includes(q))) matches.add(catId);
    });

    // While searching, open exactly the matching categories and collapse the rest
    const next = Array.from(matches);
    setOpenedCategories(next);
    prevSearchRef.current = q;
  }, [itemSearch]);

  return { itemSearch, setItemSearch, openedCategories, setOpenedCategories } as const;
}

export default UseItemAccordionSearch;
